import { useEffect, useRef, useCallback, useMemo } from 'react';
import type { Graph, Edge } from '@antv/x6';
import type { EdgeDialogInfo } from '../types';
import { safePageToLocal, getEventClientXY } from '../utils/iosCompat';
import {
  calculateObjectSize,
  calculateProcessSize,
  calculateStateSize,
  calculateValueSize,
  MIN_SIZES,
} from '../utils/sizing';
import {
  DRAG_THRESHOLD,
  clampNodeToParent,
  applyNodeSelection,
  showNodeControls,
  hideNodeControls,
  ensureConnectionPoint,
  updateOptionArrowVisibility,
} from '../utils/nodeHelpers';
import { applyParallelEdgeOffset } from '../utils/edgeOffset';
import { mergeSameTypeEdges, applySingleEdgeRouting } from '../utils/mergeSameTypeEdges';
import { getStructuralEdgeShape } from '../edges/structural';
import {
  scheduleMerge as scheduleMergeUtil,
  InvocationRefreshScheduler,
  setSuppressed,
} from '../utils/invocationRefresh';
import { useClipboard } from './useClipboard';
import { useRubberbandSelection } from './useRubberbandSelection';
import { useEdgeInteractions } from './useEdgeInteractions';

export const setSuppressManualVertices = (value: boolean) => {
  setSuppressed(value);
};

export function useNodeInteractions(
  graphRef: React.MutableRefObject<Graph | null>,
  graph: Graph | null,
  options: {
    setStatusText: (text: string) => void;
    showEdgeDialog: (info: EdgeDialogInfo) => void;
    onNodeSelect?: (node: any) => void;
  },
) {
  const { setStatusText, showEdgeDialog, onNodeSelect } = options;

  const setStatusTextRef = useRef(setStatusText);
  setStatusTextRef.current = setStatusText;

  const showEdgeDialogRef = useRef(showEdgeDialog);
  showEdgeDialogRef.current = showEdgeDialog;

  const onNodeSelectRef = useRef(onNodeSelect);
  onNodeSelectRef.current = onNodeSelect;

  const setStatusTextCb = useCallback((text: string) => {
    setStatusTextRef.current(text);
  }, []);

  const onNodeSelectCb = useCallback((node: any) => {
    onNodeSelectRef.current?.(node);
  }, []);

  // ---- drag state refs ----
  const dragSourceNodeRef = useRef<any>(null);
  const dragTempEdgeRef = useRef<Edge | null>(null);
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const dragOriginalPosRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const isMovingNodeRef = useRef(false);
  const lastLocalPosRef = useRef({ x: 0, y: 0 });
  const highlightedTargetRef = useRef<any>(null);
  const pendingEdgeRef = useRef<Edge | null>(null);
  const groupDragNodesRef = useRef<Array<{ node: any; originalPos: { x: number; y: number } }> | null>(null);
  const dragSourcePortRef = useRef<string | undefined>(undefined);
  const hiddenMoveGuideEdgeIdsRef = useRef<string[]>([]);

  // Refs to break circular dependency between cleanGraphSelection and hooks that need it
  const clearSelectedEdgeRef = useRef<() => void>(() => {});
  const clearSelectionResultRef = useRef<() => void>(() => {});

  // ---- selection helpers ----
  const cleanGraphSelection = useCallback(() => {
    if (!graph) return;
    graph.getNodes().forEach((n: any) => {
      n.setData({ selectionMode: undefined });
      applyNodeSelection(n, false);
    });
    clearSelectedEdgeRef.current();
    clearSelectionResultRef.current();
  }, [graph]);

  const selectGraphNode = useCallback((node: any, mode: 'single' | 'group' = 'single') => {
    node.setData({ selectionMode: mode });
    applyNodeSelection(node, true);
  }, []);

  const getSelectedNodes = useCallback(() => {
    if (!graph) return [];
    return graph.getNodes().filter((n: any) => n.getData()?.selected);
  }, [graph]);

  const getNodesByIds = useCallback((ids: string[]) => {
    if (!graph) return [];
    return ids
      .map(id => graph.getCellById(id))
      .filter((cell: any) => cell?.isNode?.());
  }, [graph]);

  // ---- rubberband selection ----
  const rubberband = useRubberbandSelection({
    graph,
    cleanGraphSelection,
    selectGraphNode,
    onNodeSelect: onNodeSelectCb,
    setStatusText: setStatusTextCb,
  });
  clearSelectionResultRef.current = () => {
    rubberband.selectionResultBoundsRef.current = null;
    rubberband.updateSelectionResultBox();
  };

  // ---- clipboard ----
  const clipboard = useClipboard({
    graph,
    getSelectedNodes,
    getNodesByIds,
    cleanGraphSelection,
    selectGraphNode,
    updateSelectionBoxFromNodes: rubberband.updateSelectionBoxFromNodes,
    setStatusText: setStatusTextCb,
    onNodeSelect: onNodeSelectCb,
  });

  // ---- drag visual ----
  const setDragTargetVisual = useCallback((node: any, active: boolean) => {
    if (!node) return;
    const prev = node.getData?.() || {};
    node.setData({ ...prev, dragTarget: active });
    if (prev.isParentCopy) {
      node.attr?.('body/stroke', active ? '#fadb14' : '#40a9ff');
      node.attr?.('body/strokeWidth', active ? 3 : 2);
    }
  }, []);

  // ---- pickHoverTarget ----
  const pickHoverTarget = useCallback((point: { x: number; y: number }) => {
    if (!graph) return null;
    const pad = 10;
    const sourceParentId = dragSourceNodeRef.current?.getParent?.()?.id;
    const candidates: Array<{ node: any; area: number; z: number; isParentCopy: boolean; preferredParentCopy: boolean }> = [];

    graph.getNodes().forEach((n: any) => {
      if (n === dragSourceNodeRef.current) return;
      const bbox = n.getBBox();
      const data = n.getData?.() || {};
      const within =
        point.x >= bbox.x - pad &&
        point.x <= bbox.x + bbox.width + pad &&
        point.y >= bbox.y - pad &&
        point.y <= bbox.y + bbox.height + pad;
      if (!within) return;

      if (data.isParentCopy) {
        const isEllipseParent = data.type === 'process' || n.shape === 'opm-process';
        if (isEllipseParent) {
          const cx = bbox.x + bbox.width / 2;
          const cy = bbox.y + bbox.height / 2;
          const rx = Math.max(1, bbox.width / 2);
          const ry = Math.max(1, bbox.height / 2);
          const nx = (point.x - cx) / rx;
          const ny = (point.y - cy) / ry;
          const r = Math.sqrt(nx * nx + ny * ny);
          const ringPx = 10;
          const tol = ringPx / Math.max(1, Math.min(rx, ry));
          if (Math.abs(r - 1) > tol) return;
        } else {
          const ring = 14;
          const insideInner =
            point.x >= bbox.x + ring &&
            point.x <= bbox.x + bbox.width - ring &&
            point.y >= bbox.y + ring &&
            point.y <= bbox.y + bbox.height - ring;
          if (insideInner) return;
        }
      }

      const area = Math.max(1, bbox.width * bbox.height);
      const zIndex = typeof n.getZIndex === 'function' ? n.getZIndex() ?? 0 : 0;
      candidates.push({
        node: n,
        area,
        z: zIndex,
        isParentCopy: !!data.isParentCopy,
        preferredParentCopy: !!data.isParentCopy && !!sourceParentId && n.id === sourceParentId,
      });
    });

    if (!candidates.length) return null;
    candidates.sort((a, b) => {
      if (a.preferredParentCopy !== b.preferredParentCopy) return a.preferredParentCopy ? -1 : 1;
      if (a.isParentCopy !== b.isParentCopy) return a.isParentCopy ? 1 : -1;
      if (a.area !== b.area) return a.area - b.area;
      return b.z - a.z;
    });
    return candidates[0].node;
  }, [graph]);

  // ---- invocation refresh scheduler ----
  const refreshScheduler = useMemo(() => {
    if (!graph) return null;
    return new InvocationRefreshScheduler(graph);
  }, [graph]);

  const scheduleGuideRefresh = useCallback(() => {
    refreshScheduler?.scheduleGuideRefresh();
  }, [refreshScheduler]);

  const queueInvocationBoundaryRefresh = useCallback((edge: Edge) => {
    refreshScheduler?.queueInvocationBoundaryRefresh(edge);
  }, [refreshScheduler]);

  const scheduleMerge = useCallback(() => {
    if (!graph) return;
    scheduleMergeUtil(graph);
  }, [graph]);

  const hideInvocationGuidesDuringMove = useCallback(() => {
    refreshScheduler?.hideInvocationGuidesDuringMove(hiddenMoveGuideEdgeIdsRef);
  }, [refreshScheduler]);

  const restoreInvocationGuidesAfterMove = useCallback(() => {
    refreshScheduler?.restoreInvocationGuidesAfterMove(hiddenMoveGuideEdgeIdsRef);
  }, [refreshScheduler]);

  // ---- edge interactions ----
  const edgeInteractions = useEdgeInteractions({
    graph,
    dragSourceNodeRef,
    dragTempEdgeRef,
    highlightedTargetRef,
    setDragTargetVisual,
    pickHoverTarget,
    scheduleGuideRefresh,
    queueInvocationBoundaryRefresh,
    setStatusText: setStatusTextCb,
  });
  clearSelectedEdgeRef.current = edgeInteractions.clearSelectedEdge;

  // ---- finishDrag ----
  const finishDrag = useCallback((localX?: number, localY?: number) => {
    if (!graph) return;
    const dragSourceNode = dragSourceNodeRef.current;
    const dragTempEdge = dragTempEdgeRef.current;
    const isDragging = isDraggingRef.current;
    const isMovingNode = isMovingNodeRef.current;
    const lastLocalPos = lastLocalPosRef.current;

    if (isMovingNode) {
      if (groupDragNodesRef.current) {
        applyParallelEdgeOffset(graph);
        scheduleMerge();
        if (rubberband.selectionResultBoundsRef.current && groupDragNodesRef.current.length > 0) {
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          groupDragNodesRef.current.forEach(({ node: gNode }) => {
            const pos = gNode.getPosition();
            const size = gNode.getSize();
            minX = Math.min(minX, pos.x);
            minY = Math.min(minY, pos.y);
            maxX = Math.max(maxX, pos.x + size.width);
            maxY = Math.max(maxY, pos.y + size.height);
          });
          rubberband.selectionResultBoundsRef.current = { minX, minY, maxX, maxY };
          rubberband.updateSelectionResultBox();
        }
        groupDragNodesRef.current = null;
      } else {
        applyParallelEdgeOffset(graph);
        scheduleMerge();
      }
      dragSourceNodeRef.current = null;
      dragTempEdgeRef.current = null;
      isDraggingRef.current = false;
      isMovingNodeRef.current = false;
      restoreInvocationGuidesAfterMove();
      setStatusTextRef.current('节点已移动');
      graph.enablePanning();
      return;
    }

    if (!isDragging || !dragTempEdge || !dragSourceNode) {
      dragSourceNodeRef.current = null;
      dragTempEdgeRef.current = null;
      isDraggingRef.current = false;
      graph.enablePanning();
      return;
    }

    const targetPos = (localX !== undefined && localY !== undefined)
      ? { x: localX, y: localY }
      : lastLocalPos;

    const targetNode = pickHoverTarget(targetPos);

    if (highlightedTargetRef.current) {
      setDragTargetVisual(highlightedTargetRef.current, false);
      highlightedTargetRef.current = null;
    }

    const isFromArrowPort = dragSourcePortRef.current === 'port-arrow';

    if (targetNode && targetNode !== dragSourceNode) {
      if (isFromArrowPort) {
        if (dragTempEdge && graph.hasCell(dragTempEdge.id)) {
          graph.removeCell(dragTempEdge.id);
        }
        const targetIsOption = targetNode.getData?.()?.type === 'option';
        const targetCfg: any = targetIsOption
          ? ensureConnectionPoint({ cell: targetNode.id, port: 'port-arrow' })
          : { cell: targetNode.id, connectionPoint: { name: 'boundary' } };
        const sourceCfg: any = { cell: dragSourceNode.id };
        let stubVertices: { x: number; y: number }[] = [];
        if (isFromArrowPort) {
          sourceCfg.port = 'port-arrow';
          sourceCfg.anchor = { name: 'top' };
          sourceCfg.connectionPoint = { name: 'anchor' };
          const srcBox = dragSourceNode.getBBox();
          const srcCx = srcBox.x + srcBox.width / 2;
          const stubY = srcBox.y - 32;
          const tgtBox = targetNode.getBBox();
          const tgtCx = tgtBox.x + tgtBox.width / 2;
          stubVertices = [
            { x: srcCx, y: stubY },
            { x: tgtCx, y: stubY },
          ];
        }
        const structuralShape = getStructuralEdgeShape('aggregation-participation');
        const createdEdge = graph.addEdge({
          shape: structuralShape,
          source: sourceCfg,
          target: targetCfg,
          router: { name: 'orth', args: { padding: 20 } },
          connector: { name: 'jumpover', args: { size: 5 } },
          vertices: stubVertices,
          data: {
            edgeCategory: 'structural',
            edgeSubtype: 'aggregation-participation',
            label: 'aggregation-participation',
            ...(isFromArrowPort ? { isOptionArrowEdge: true } : {}),
          },
          attrs: {
            line: {
              stroke: '#595959',
              strokeWidth: 1.8,
              targetMarker: {
                name: 'path',
                args: {
                  d: 'M 0 8 L 14 0 L 14 16 Z',
                  fill: 'none',
                  stroke: '#595959',
                  strokeWidth: 1.5,
                },
              },
            },
          },
        });
        if (isFromArrowPort && createdEdge?.isEdge?.()) {
          setSuppressed(true);
          applySingleEdgeRouting(graph, createdEdge, 'top', false, true);
          setSuppressed(false);
        }
        applyParallelEdgeOffset(graph);
        mergeSameTypeEdges(graph);
        updateOptionArrowVisibility(dragSourceNode, graph);
        updateOptionArrowVisibility(targetNode, graph);
        setStatusTextRef.current(`已创建连线: ${dragSourceNode.getData()?.label || dragSourceNode.id} → ${targetNode.getData()?.label || targetNode.id}`);
      } else {
        const sourceLabel = dragSourceNode.getData()?.label || dragSourceNode.id;
        const targetLabel = targetNode.getData()?.label || targetNode.id;
        const sourceData = dragSourceNode.getData?.() || {};
        const targetData = targetNode.getData?.() || {};
        const sourceNodeType = sourceData.isParentCopy ? 'process' : (sourceData.type || 'object');
        const targetNodeType = targetData.isParentCopy ? 'process' : (targetData.type || 'object');
        pendingEdgeRef.current = dragTempEdge;
        showEdgeDialogRef.current({
          edge: dragTempEdge,
          sourceId: dragSourceNode.id,
          targetId: targetNode.id,
          targetPoint: targetPos,
          sourceLabel,
          targetLabel,
          sourceNodeType,
          targetNodeType,
          sourcePort: dragSourcePortRef.current,
        });
        setStatusTextRef.current('请选择连线类型');
      }
    } else {
      if (dragTempEdge && graph.hasCell(dragTempEdge.id)) {
        graph.removeCell(dragTempEdge.id);
      }
      updateOptionArrowVisibility(dragSourceNode, graph);
      setStatusTextRef.current('未连接到目标节点，连线已取消');
    }

    dragSourceNodeRef.current = null;
    dragTempEdgeRef.current = null;
    isDraggingRef.current = false;
    dragSourcePortRef.current = undefined;
    graph.enablePanning();
  }, [graph, pickHoverTarget, setDragTargetVisual, scheduleMerge, restoreInvocationGuidesAfterMove, rubberband]);

  // ---- handleNodeDragMove ----
  const handleNodeDragMove = useCallback((args: any) => {
    if (!graph) return;
    const node = dragSourceNodeRef.current;
    if (!node) return;
    const e: MouseEvent = args.e;
    if (e.ctrlKey || e.metaKey) return;

    const { clientX, clientY } = getEventClientXY(e);
    const localPoint = safePageToLocal(graph, clientX, clientY);
    lastLocalPosRef.current = { x: localPoint.x, y: localPoint.y };
    const dist = Math.hypot(clientX - dragStartPosRef.current.x, clientY - dragStartPosRef.current.y);

    if (!isDraggingRef.current && dist >= DRAG_THRESHOLD) {
      isDraggingRef.current = true;
      if (!isMovingNodeRef.current) {
        node.setPosition(dragOriginalPosRef.current);
        const sourceConfig: any = { cell: node.id };
        if (dragSourcePortRef.current) {
          sourceConfig.port = dragSourcePortRef.current;
          ensureConnectionPoint(sourceConfig);
        }
        const isFromArrow = dragSourcePortRef.current === 'port-arrow';
        if (isFromArrow && node.hasPort('port-arrow')) {
          node.setPortProp('port-arrow', 'attrs/arrowLine/opacity', 0);
          node.setPortProp('port-arrow', 'attrs/arrowHead/opacity', 0);
        }
        dragTempEdgeRef.current = graph.addEdge({
          shape: 'edge',
          source: sourceConfig,
          target: { x: localPoint.x, y: localPoint.y },
          router: isFromArrow
            ? { name: 'orth', args: { padding: 20 } }
            : { name: 'normal' },
          connector: { name: 'normal' },
          attrs: {
            line: {
              stroke: '#595959',
              strokeWidth: 1.8,
              strokeDasharray: '5 5',
              targetMarker: isFromArrow
                ? { name: 'classic', width: 12, height: 12, fill: '#595959', stroke: '#595959' }
                : { name: 'block', width: 8, height: 8, fill: '#595959', stroke: '#595959' },
            },
          },
          zIndex: 9999,
        });
        setStatusTextRef.current('拖拽到目标节点松开以创建连线...');
      } else {
        hideInvocationGuidesDuringMove();
        setStatusTextRef.current(`移动节点: ${node.getData()?.label || node.id}`);
      }
    }

    if (isDraggingRef.current) {
      if (!isMovingNodeRef.current && dragTempEdgeRef.current) {
        node.setPosition(dragOriginalPosRef.current);
        dragTempEdgeRef.current.setTarget({ x: localPoint.x, y: localPoint.y });
        const hoverTarget = pickHoverTarget(localPoint);
        if (hoverTarget !== highlightedTargetRef.current) {
          if (highlightedTargetRef.current) setDragTargetVisual(highlightedTargetRef.current, false);
          if (hoverTarget) setDragTargetVisual(hoverTarget, true);
          highlightedTargetRef.current = hoverTarget;
        }
      } else if (isMovingNodeRef.current) {
        const dx = clientX - dragStartPosRef.current.x;
        const dy = clientY - dragStartPosRef.current.y;
        const zoom = graph.zoom();
        const targetX = dragOriginalPosRef.current.x + dx / zoom;
        const targetY = dragOriginalPosRef.current.y + dy / zoom;

        if (groupDragNodesRef.current && groupDragNodesRef.current.length > 1) {
          const deltaX = targetX - dragOriginalPosRef.current.x;
          const deltaY = targetY - dragOriginalPosRef.current.y;
          groupDragNodesRef.current.forEach(({ node: gNode, originalPos }) => {
            gNode.setPosition(originalPos.x + deltaX, originalPos.y + deltaY);
          });
          return;
        }

        const currentPos = node.getPosition();
        const moveDx = targetX - currentPos.x;
        const moveDy = targetY - currentPos.y;
        node.translate(moveDx, moveDy);

        const connected = graph.getConnectedEdges(node) as Edge[];
        connected.forEach(edge => {
          const edgeData = edge.getData?.() || {};
          if (edgeData._manualVertices) {
            edge.setData({ ...edgeData, _manualVertices: false });
          }
        });

        const parentNode = node.getParent();
        if (parentNode) {
          const parentType = parentNode.getData()?.type;
          const parentLabel = parentNode.getData()?.label || '';

          let minSize: { width: number; height: number };
          if (parentType === 'process') {
            minSize = calculateProcessSize(parentLabel);
          } else if (parentType === 'object' || parentType === 'option') {
            minSize = calculateObjectSize(parentLabel, false);
          } else if (parentType === 'state') {
            minSize = calculateStateSize(parentLabel);
          } else if (parentType === 'value') {
            minSize = calculateValueSize(parentLabel);
          } else {
            minSize = MIN_SIZES[parentType] || MIN_SIZES.object;
          }

          const padding = parentType === 'process'
            ? { top: 60, left: 50, right: 50, bottom: 60 }
            : { top: 50, left: 20, right: 20, bottom: 20 };

          parentNode.fit({ padding });

          const parentSize = parentNode.getSize();
          if (parentSize.width < minSize.width || parentSize.height < minSize.height) {
            const newWidth = Math.max(parentSize.width, minSize.width);
            const newHeight = Math.max(parentSize.height, minSize.height);
            parentNode.setSize({ width: newWidth, height: newHeight });
          }

          if (parentType === 'process') {
            const children = parentNode.getChildren()?.filter((c: any) => typeof c.isNode === 'function' && c.isNode()) || [];
            const pp = parentNode.getPosition();
            const ps = parentNode.getSize();
            const cx = pp.x + ps.width / 2;
            const cy = pp.y + ps.height / 2;
            let rx = ps.width / 2;
            let ry = ps.height / 2;
            const aspect = rx / ry;
            let needExpand = false;

            children.forEach((child: any) => {
              const cp = child.getPosition();
              const cs = child.getSize();
              const corners = [
                { x: cp.x, y: cp.y },
                { x: cp.x + cs.width, y: cp.y },
                { x: cp.x, y: cp.y + cs.height },
                { x: cp.x + cs.width, y: cp.y + cs.height },
              ];
              corners.forEach(({ x, y }) => {
                const ddx = x - cx;
                const ddy = y - cy;
                const dist = (ddx * ddx) / (rx * rx) + (ddy * ddy) / (ry * ry);
                if (dist > 1) {
                  needExpand = true;
                  const newRy = Math.sqrt((ddx * ddx) / (aspect * aspect) + ddy * ddy);
                  const newRx = aspect * newRy;
                  if (newRx > rx) { rx = newRx; ry = newRy; }
                }
              });
            });

            if (needExpand) {
              parentNode.setPosition(cx - rx, cy - ry);
              parentNode.setSize({ width: rx * 2, height: ry * 2 });
            }
          }

          const newParentPos = parentNode.getPosition();
          const relX = node.getPosition().x - newParentPos.x;
          const relY = node.getPosition().y - newParentPos.y;
          const clamped = clampNodeToParent(node, parentNode, relX, relY);
          node.setPosition(newParentPos.x + clamped.x, newParentPos.y + clamped.y);
        }
      }
    }
  }, [graph, pickHoverTarget, setDragTargetVisual, hideInvocationGuidesDuringMove]);

  // ---- main effect: register all graph events ----
  useEffect(() => {
    if (!graph || !graphRef.current) return;

    const containerElement = graph.container;
    const updateCursor = (value: string) => {
      if (containerElement) containerElement.style.cursor = value;
    };

    // ---- node hover ----
    graph.on('node:mouseenter', ({ node }) => {
      showNodeControls(node);
      updateCursor('move');
    });

    graph.on('node:mouseleave', ({ node }) => {
      hideNodeControls(node);
      updateCursor('default');
    });

    graph.on('node:port:mouseenter', () => updateCursor('crosshair'));
    graph.on('node:port:mouseleave', () => updateCursor('move'));

    // ---- node mousedown ----
    graph.on('node:mousedown', (args: any) => {
      const node = args.node;
      const e: MouseEvent | TouchEvent = args.e;
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      e.stopPropagation();
      graph.disablePanning();

      const target = (e.target || e.srcElement) as Element | null;
      const isTitleOrLabel = !!target && (
        target.classList?.contains('opm-node-title') ||
        target.classList?.contains('opm-node-label') ||
        !!target.closest?.('.opm-node-title, .opm-node-label')
      );
      const isPortHandle = !!target && !!target.closest?.('.opm-node-port');

      let sourcePortId: string | undefined;
      if (isPortHandle) {
        const portEl = target.closest('[port]') as Element | null;
        sourcePortId = portEl?.getAttribute('port') || undefined;
      }
      dragSourcePortRef.current = sourcePortId;

      const nodeType = node.getData()?.type;
      const isChildNode = nodeType === 'state' || nodeType === 'value';
      const fromTextArea = !!(window as any).__OPM_TEXT_AREA_MOUSEDOWN;
      (window as any).__OPM_TEXT_AREA_MOUSEDOWN = false;
      const isOptionNode = nodeType === 'option';
      const isMoveMode = !isPortHandle && (isTitleOrLabel || isChildNode || fromTextArea || isOptionNode);

      const { clientX, clientY } = getEventClientXY(e);
      dragStartPosRef.current = { x: clientX, y: clientY };
      dragOriginalPosRef.current = { ...node.getPosition() };
      dragSourceNodeRef.current = node;
      isDraggingRef.current = false;
      isMovingNodeRef.current = isMoveMode;

      const nodeData = node.getData();
      if (isMoveMode && nodeData?.selected && nodeData?.selectionMode === 'group') {
        const selectedNodes = getSelectedNodes().filter((n: any) => {
          const parent = n.getParent?.();
          return !parent;
        });
        if (selectedNodes.length > 1) {
          groupDragNodesRef.current = selectedNodes.map((n: any) => ({
            node: n,
            originalPos: { ...n.getPosition() },
          }));
        }
      }
    });

    // ---- mousemove (blank + cell) ----
    graph.on('blank:mousemove', handleNodeDragMove);
    graph.on('cell:mousemove', handleNodeDragMove);

    // ---- selection events ----
    graph.on('node:selected', ({ node }) => applyNodeSelection(node, true));
    graph.on('node:unselected', ({ node }) => applyNodeSelection(node, false));

    // ---- click events ----
    graph.on('node:click', (args: any) => {
      const node = args.node;
      if (isDraggingRef.current) return;
      const { clientX, clientY } = getEventClientXY(args.e);
      clipboard.lastCanvasClickRef.current = safePageToLocal(graph, clientX, clientY);
      if (rubberband.suppressNodeClickRef.current) {
        rubberband.suppressNodeClickRef.current = false;
        return;
      }
      cleanGraphSelection();
      selectGraphNode(node, 'single');
      onNodeSelectRef.current?.(node);
      setStatusTextRef.current(`选中: ${node.getData()?.label || node.id} (可拖拽移动)`);
    });

    graph.on('node:dblclick', (args: any) => {
      const node = args.node;
      const { clientX, clientY } = getEventClientXY(args.e);
      clipboard.lastCanvasClickRef.current = safePageToLocal(graph, clientX, clientY);
      if (rubberband.suppressNodeClickRef.current) {
        rubberband.suppressNodeClickRef.current = false;
        return;
      }
      cleanGraphSelection();
      selectGraphNode(node, 'single');
      onNodeSelectRef.current?.(node);
      setStatusTextRef.current(`双击选中: ${node.getData()?.label || node.id}`);
    });

    graph.on('blank:click', (args: any) => {
      const { clientX, clientY } = getEventClientXY(args.e);
      clipboard.lastCanvasClickRef.current = safePageToLocal(graph, clientX, clientY);
      if (rubberband.suppressBlankClickRef.current) {
        rubberband.suppressBlankClickRef.current = false;
        return;
      }
      cleanGraphSelection();
      onNodeSelectRef.current?.(null);
      setStatusTextRef.current('就绪');
    });

    graph.on('blank:mousedown', (args: any) => {
      const e: MouseEvent = args.e;
      if (e.altKey || e.button !== 0) return;
      clipboard.lastCanvasClickRef.current = safePageToLocal(graph, e.clientX, e.clientY);
      rubberband.beginSelection(e.clientX, e.clientY);
    });

    // ---- mouseup events ----
    graph.on('node:mouseup', (args: any) => {
      const { clientX, clientY } = getEventClientXY(args.e);
      const localPoint = safePageToLocal(graph, clientX, clientY);
      finishDrag(localPoint.x, localPoint.y);
    });

    graph.on('blank:mouseup', (args: any) => {
      const { clientX, clientY } = getEventClientXY(args.e);
      if (rubberband.handleSelectionMouseUp(clientX, clientY)) return;
      const localPoint = safePageToLocal(graph, clientX, clientY);
      finishDrag(localPoint.x, localPoint.y);
    });

    // ---- global mouseup ----
    const handleWindowMouseUp = (e: MouseEvent | TouchEvent) => {
      const { clientX, clientY } = getEventClientXY(e);
      if (rubberband.handleSelectionMouseUp(clientX, clientY)) return;
      if (!isDraggingRef.current) return;
      const localPoint = safePageToLocal(graph, clientX, clientY);
      finishDrag(localPoint.x, localPoint.y);
    };
    window.addEventListener('mouseup', handleWindowMouseUp);
    window.addEventListener('touchend', handleWindowMouseUp);

    // ---- global mousemove ----
    const handleGlobalMouseMove = (e: MouseEvent | TouchEvent) => {
      const { clientX, clientY } = getEventClientXY(e);
      rubberband.handleSelectionMouseMove(clientX, clientY);
      if (isDraggingRef.current && dragTempEdgeRef.current) {
        const localPoint = safePageToLocal(graph, clientX, clientY);
        lastLocalPosRef.current = { x: localPoint.x, y: localPoint.y };
        dragTempEdgeRef.current.setTarget({ x: localPoint.x, y: localPoint.y });
        if (dragSourceNodeRef.current) {
          dragSourceNodeRef.current.setPosition(dragOriginalPosRef.current);
        }
        const hoverTarget = pickHoverTarget(localPoint);
        if (hoverTarget !== highlightedTargetRef.current) {
          if (highlightedTargetRef.current) setDragTargetVisual(highlightedTargetRef.current, false);
          if (hoverTarget) setDragTargetVisual(hoverTarget, true);
          highlightedTargetRef.current = hoverTarget;
        }
      }
    };
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('touchmove', handleGlobalMouseMove, { passive: false });

    // ---- keyboard shortcuts ----
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        clipboard.copySelectedNodes();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        clipboard.pasteClipboardNodes();
        return;
      }
      if (e.key === 'Escape' && isDraggingRef.current && dragTempEdgeRef.current) {
        if (highlightedTargetRef.current) {
          setDragTargetVisual(highlightedTargetRef.current, false);
          highlightedTargetRef.current = null;
        }
        if (graph.hasCell(dragTempEdgeRef.current.id)) {
          graph.removeCell(dragTempEdgeRef.current.id);
        }
        if (dragSourceNodeRef.current) {
          dragSourceNodeRef.current.setPosition(dragOriginalPosRef.current);
          updateOptionArrowVisibility(dragSourceNodeRef.current, graph);
        }
        dragSourceNodeRef.current = null;
        dragTempEdgeRef.current = null;
        isDraggingRef.current = false;
        dragSourcePortRef.current = undefined;
        setStatusTextRef.current('拖拽已取消');
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const activeEl = document.activeElement as HTMLElement | null;
        const isTypingTarget = !!activeEl &&
          (activeEl.tagName === 'INPUT' ||
            activeEl.tagName === 'TEXTAREA' ||
            activeEl.getAttribute('contenteditable') === 'true');
        if (isTypingTarget) return;
        const selectedNodes = getSelectedNodes();
        let removed = false;
        if (selectedNodes.length) {
          selectedNodes.forEach(node => node.remove());
          removed = true;
        }
        if (edgeInteractions.selectedEdgeRef.current) {
          edgeInteractions.selectedEdgeRef.current.remove();
          edgeInteractions.clearSelectedEdge();
          removed = true;
        }
        if (removed) {
          setStatusTextRef.current('已删除选中的元素');
        }
        return;
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    // ---- viewport change ----
    const handleViewportChange = () => {
      rubberband.updateSelectionResultBox();
    };
    graph.on('scale', handleViewportChange);
    graph.on('translate', handleViewportChange);

    // ---- initial status ----
    setStatusTextRef.current(
      `就绪 — ${graph.getNodes().length} 节点 | 点击节点空白处拖拽创建连线，点击标题/子状态可拖动节点，按住 Alt+左键拖动画布，左键框选可批量选中`,
    );

    return () => {
      refreshScheduler?.cancel();
      window.removeEventListener('mouseup', handleWindowMouseUp);
      window.removeEventListener('touchend', handleWindowMouseUp);
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('touchmove', handleGlobalMouseMove);
      window.removeEventListener('keydown', handleKeyDown);
      graph.off('blank:mousemove', handleNodeDragMove);
      graph.off('cell:mousemove', handleNodeDragMove);
      graph.off('scale', handleViewportChange);
      graph.off('translate', handleViewportChange);
      rubberband.cleanup();
    };
  }, [graph]);

  return { pendingEdgeRef };
}
